import React, {useState, useRef, useMemo, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Box,
    Text,
    Flex,
    Skeleton,
    useMultiStyleConfig
} from '@chakra-ui/react'

import {ArrowIcon} from '../../../../components/icons'
import textStyles from '../../../../theme/foundations/textStyles'
import lazyLoadable from '../../../../utils/lazy-loadable'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import {RECOMMENDED_SPARE_PARTS_TYPE} from '../../../../constants'
import {hasWhatsIncluded} from './utils'

const ScrollParentIntoView = ({enabled}) => {
    const [el, setEl] = useState(null) // state to trigger the effect

    useEffect(() => {
        if (enabled && el) {
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }
    }, [enabled, el])

    return (
        <Box
            ref={(node) => {
                if (node) {
                    // This travels up the DOM to find the actual AccordionItem container
                    const parentItem =
                        node.closest('.chakra-accordion__item') || node.parentElement?.parentElement
                    if (parentItem) setEl(parentItem)
                }
            }}
            height={0}
            width={0}
            style={{visibility: 'hidden', position: 'absolute'}}
        />
    )
}

ScrollParentIntoView.propTypes = {
    enabled: PropTypes.bool
}

// Lazy loaded sub-panels
const fallback = <Skeleton height="25vh" width="100%" />
const Specifications = lazyLoadable(() => import('./specifications'), {
    fallback,
    ssr: false
})
const WhatsIncluded = lazyLoadable(() => import('./whats-included'), {
    fallback,
    ssr: false
})
const HowToUse = lazyLoadable(() => import('./how-to-use'), {fallback, ssr: false})
const SpareParts = lazyLoadable(() => import('./spare-parts'), {fallback, ssr: false})

const ProductDetailsAccordion = ({product, onRecommendedProductClick}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('AfterSales')
    const {brightcoveEnabled} = useOcapiSite()
    const howToUseRef = useRef(null)

    const _hasWhatsIncluded = useMemo(() => hasWhatsIncluded(product), [product])
    const hasSpareParts = product?.recommendations?.some(
        ({recommendationType}) => recommendationType.value === RECOMMENDED_SPARE_PARTS_TYPE
    )
    const hasFaqs =
        (product?.c_assemblyVideos?.[0] && brightcoveEnabled) ||
        product?.c_maintenance ||
        product?.c_documents

    const [hasChanged, setHasChanged] = useState(false)

    const handleOnChange = () => {
        setHasChanged(true)
    }

    if (!product) return null

    return (
        <Box {...styles.container} background="stokkeGray.bright">
            <Accordion onChange={handleOnChange} allowToggle>
                {/* 0. Specifications */}
                <AccordionItem {...styles.accordionItem}>
                    {({isExpanded}) => (
                        <>
                            <Text as="h3">
                                <AccordionButton
                                    {...styles.accordionButton}
                                    {...textStyles['Regular XLarge']}
                                >
                                    {intl.formatMessage({
                                        id: 'pdp.module.specifications',
                                        defaultMessage: 'Specifications'
                                    })}
                                    <ArrowIcon
                                        boxSize={4}
                                        transform={isExpanded && 'rotate(-180deg)'}
                                        transition=".2s"
                                    />
                                </AccordionButton>
                            </Text>
                            <AccordionPanel {...styles.accordionPanel} mt={8}>
                                {isExpanded && (
                                    <>
                                        <ScrollParentIntoView enabled={hasChanged} />
                                        <Specifications product={product} />
                                    </>
                                )}
                            </AccordionPanel>
                        </>
                    )}
                </AccordionItem>

                {/* 1. What's Included */}
                {_hasWhatsIncluded && (
                    <AccordionItem {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <Text as="h3">
                                    <AccordionButton
                                        {...styles.accordionButton}
                                        {...textStyles['Regular XLarge']}
                                    >
                                        {intl.formatMessage({
                                            id: 'pdp.module.specifications.included',
                                            defaultMessage: "What's included"
                                        })}
                                        <ArrowIcon
                                            boxSize={4}
                                            transform={isExpanded && 'rotate(-180deg)'}
                                            transition=".2s"
                                        />
                                    </AccordionButton>
                                </Text>
                                <AccordionPanel>
                                    {isExpanded && (
                                        <>
                                            <ScrollParentIntoView enabled={hasChanged} />
                                            <WhatsIncluded product={product} />
                                        </>
                                    )}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                )}

                {/* 2. How to Use / Documents */}
                {hasFaqs && (
                    <AccordionItem
                        {...styles.accordionItem}
                        ref={(el) => {
                            howToUseRef.current = el
                        }}
                    >
                        {({isExpanded}) => (
                            <>
                                <Text as="h3">
                                    <AccordionButton
                                        {...styles.accordionButton}
                                        data-testid="button_documents"
                                        {...styles.accordionButtonLabel}
                                        {...textStyles['Regular XLarge']}
                                    >
                                        <FormattedMessage
                                            id="product_detail.after_sales.documents"
                                            defaultMessage="How to use / Documents"
                                        />
                                        <ArrowIcon
                                            boxSize={4}
                                            transform={isExpanded && 'rotate(-180deg)'}
                                            transition=".2s"
                                        />
                                    </AccordionButton>
                                </Text>
                                <AccordionPanel>
                                    {isExpanded && (
                                        <>
                                            <ScrollParentIntoView enabled={hasChanged} />
                                            <HowToUse product={product} isExpanded={isExpanded} />
                                        </>
                                    )}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                )}

                {/* 3. Spare Parts */}
                {hasSpareParts && (
                    <AccordionItem {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <Text as="h3">
                                    <AccordionButton
                                        {...styles.accordionButton}
                                        data-testid="button_spare_parts"
                                        {...textStyles['Regular XLarge']}
                                    >
                                        <FormattedMessage
                                            id="product_detail.sections.spareParts"
                                            defaultMessage="Spare Parts"
                                        />
                                        <ArrowIcon
                                            boxSize={4}
                                            transform={isExpanded && 'rotate(-180deg)'}
                                            transition=".2s"
                                        />
                                    </AccordionButton>
                                </Text>
                                <Flex {...styles.sparePartsAccordionPanel}>
                                    <AccordionPanel paddingInline={0}>
                                        {isExpanded && (
                                            <>
                                                <ScrollParentIntoView enabled={hasChanged} />
                                                <SpareParts
                                                    product={product}
                                                    onRecommendedProductClick={
                                                        onRecommendedProductClick
                                                    }
                                                />
                                            </>
                                        )}
                                    </AccordionPanel>
                                </Flex>
                            </>
                        )}
                    </AccordionItem>
                )}
            </Accordion>
        </Box>
    )
}

ProductDetailsAccordion.propTypes = {
    product: PropTypes.object,
    onRecommendedProductClick: PropTypes.func
}

export default ProductDetailsAccordion
